/*  MUI  */
import { Box, Button, Paper, Typography } from '@mui/material';

/*  Okta  */
import { withOktaAuth } from '@okta/okta-react';
import { setSessionInfo } from 'actions';
import { BroadcastChannelContext } from 'application/App/BroadcastChannelContext';

/*  Shared  */
import { Logo } from 'application/Shared/Components/App';
import { Storage } from 'application/Shared/Handlers';
import { Helpers, Response } from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { Env, debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import Session from 'objects/Session';

/*  React  */
import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'router/withRouter';

const baseURL = `${process.env.REACT_APP_BACKEND}/api`;

const close = () => {
    window.location.href = `${window.origin}/login`;
    window.close();
};

class OktaCallback extends PureComponent {
    // eslint-disable-next-line react/static-property-placement
    static contextType = BroadcastChannelContext;

    constructor(props) {
        super(props);
        this.state = {
            error: false,
            errorMsg: null
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const searchParams = new URLSearchParams(this.props.location.search);

        // the 'state' param was set by us in Okta/Login and is returned here as a URL encoded string.
        const stateParam = searchParams.get('state');
        const decodedStateParam = decodeURIComponent(stateParam);
        const parsedStateParams = new URLSearchParams(decodedStateParam);
        const isPopup = parsedStateParams.get('popup') === 'true';
        parsedStateParams.delete('popup');

        const code = searchParams.get('code');
        try {
            const api = axios.create({
                baseURL: Env.isLocal() ? '/api' : baseURL
            });

            debug.i(`GET /authentication/oauth2/okta/code?code=${code}`);
            const response = await api.get(
                `/authentication/oauth2/okta/code?code=${code}`,
                {
                    cancelToken: this.requests.token(),
                    params: { redirectUri: `${window.origin}/okta/callback` },
                    withCredentials: true
                }
            );

            debug.i('OAuth Response', response);

            if (!Response.has(response)) {
                debug.e('No Response from Auth', response);
                this.setState({ error: true });
                setTimeout(() => window.close(), 2000);
                return;
            }

            const { data } = response;
            // eslint-disable-next-line camelcase
            const { jwt, id_token } = data;

            if (jwt) {
                await Session.storeJWT(jwt, async () => {
                    this.context.sendMessage('pageReload');

                    // If the login took place in a popup window, close it.
                    if (isPopup) {
                        window.close();
                    } else {
                        // Users directed to ASM from ANM will login via Okta with no popup.
                        // This means that we need to load the user's session before pushing them to '/' below.
                        let userSession;
                        try {
                            userSession = await Session.getSessionInfo();
                            debug.dev(
                                'OKTA => Get Session Info Response',
                                userSession
                            );
                            this.props.setSessionInfo(userSession);
                        } catch (e) {
                            debug.e('Error getting Session', e);
                        }

                        // Replaced the `window.location.href` that was here with `navigate` and
                        // all of the session loading above b/c adding the search params along with
                        // `window.location.href` was causing a Snyk error.
                        this.props.navigate({
                            pathname: '/',
                            search: parsedStateParams.toString()
                        });
                    }
                });
                /**
                 * Okta id token is stored for okta-session logout.
                 */
                Storage.set('okta_id_token', id_token, (v) => {
                    if (!v) {
                        debug.e('Failed to store', 'okta_id_token', id_token);
                    }
                });

                Storage.set('authentication-type', 'okta', (v) => {
                    if (!v) {
                        debug.e(
                            'Failed to store',
                            'authentication-type',
                            'okta'
                        );
                    }
                });
                return;
            }

            debug.e('No JWT or Action from Auth', response);
            this.setState({ error: true });
            if (isPopup) {
                setTimeout(() => window.close(), 2000);
            }
        } catch (e) {
            debug.e('OAuth Error', e);
            if (Helpers.isset(e, 'response', 'data', 'error')) {
                this.setState({ errorMsg: e.response.data.error });
            }

            this.setState({ error: true });
            if (isPopup) {
                setTimeout(() => window.close(), 2000);
            }
        }
    };

    unload = () => {
        this.requests.cancel();
    };

    render() {
        const { t } = this.props;
        const { error, errorMsg } = this.state;

        return (
            <Paper
                elevation={0}
                variant="outlined"
                style={{ height: '100%', padding: '24px' }}
            >
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Box>
                        <Logo />
                    </Box>
                    <Box my="32px" textAlign="center">
                        {error ? (
                            <>
                                <hr />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'left'
                                    }}
                                >
                                    {errorMsg || t('OAuth.error')}
                                </Typography>
                                <Button
                                    data-trackid="login_okta_close"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => close()}
                                    style={{ marginTop: '32px' }}
                                >
                                    {t('OAuth.close')}
                                </Button>
                            </>
                        ) : (
                            <Typography
                                variant="h3"
                                sx={{ color: 'text.tertiary' }}
                            >
                                {t('OAuth.wait')}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    setSessionInfo: (sessionInfo) => dispatch(setSessionInfo(sessionInfo))
});

export default connect(
    null,
    mapDispatchToProps
)(withOktaAuth(withRouter(withTranslation()(OktaCallback))));
